import { isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { ContextMenuOptions } from '../../../components/context-menu-global/context-menu-global.component';
import { sleep } from '../../../lib/sleep';
import { ContextMenuService } from '../../../services/context-menu.service';

@Component({
  selector: 'app-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class ContextMenuComponent implements OnInit, OnDestroy {
  private contextMenuService = inject(ContextMenuService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);

  @Input() position:
    | 'auto'
    | 'left'
    | 'left-out'
    | 'center'
    | 'screen-center'
    | 'screen-right'
    | 'right'
    | 'right-out' = 'auto';
  @Input() verticalPosition = 'auto';
  @Input() options: ContextMenuOptions = {};
  @Input() contextHoverIdentifier: string;
  @Input() contextHoverIdentifiers: string[];
  @Input() clientEl?: HTMLElement;
  @Input() disabled: boolean;
  @Output() onToggle = new EventEmitter();
  @ViewChild('contextMenu') contextMenu: TemplateRef<unknown>;
  @ViewChild('contextButton') contextButton: ElementRef;
  public _isOpen = signal(false);
  public isOpen = signal(false);
  private hover: boolean;
  private hoverDelayStart: number;
  lastOpenTime: number;
  destroyed$ = new Subject<void>();

  @HostListener('mouseover') async enter() {
    if (this.disabled) return;
    if (this.hover) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    if (this.options.openOnHover) {
      this.hover = true;
      if (this.options.hoverDelay) {
        const start = Date.now();
        this.hoverDelayStart = start;
        await sleep(this.options.hoverDelay);
        if (this.hoverDelayStart !== start) return;
        if (!this.hover) return;
      }
      this.open();
    }
  }
  @HostListener('mouseleave') async leave() {
    if (this.disabled) return;
    if (ScreenSizes.screenSize() !== 'desktop') return;
    this.hover = false;
    if (this.lastOpenTime && Date.now() - this.lastOpenTime < 200) await sleep(200);
    await sleep(0);
    if (this.contextMenuService.onContextContentHover.value) return;
    if (this.options.openOnHover) this.close();
  }

  ngOnInit(): void {
    if (isPlatformServer(this.platformId)) return;
    this.contextMenuService.onContextContentHover.pipe(takeUntil(this.destroyed$)).subscribe(async hover => {
      if (hover !== false) return;
      await sleep(0);
      if (this.hover) return;
      if (ScreenSizes.screenSize() !== 'desktop') return;
      if (this.options.openOnHover) this.close();
    });
  }

  clickOpen(event: MouseEvent) {
    if (this.disabled) return;
    if (!this.options.allowClicks) event.preventDefault();
    if (this.options.openOnHover && ScreenSizes.screenSize() === 'desktop') return;
    if (!this.options.allowClicks) event?.stopPropagation();
    if (this.contextMenuService.onContextOpen.value && this.contextMenuService.onContextOpen.value.component === this) {
      return this.close();
    }
    this.open();
  }

  async open() {
    this.lastOpenTime = Date.now();
    const options = { ...this.options };
    if (ScreenSizes.screenSize() !== 'desktop') delete options.openOnHover;

    const clientEl = this.clientEl || this.contextButton?.nativeElement;
    if (!clientEl) return;

    const resp = await this.contextMenuService.showContext({
      component: this,
      template: this.contextMenu,
      position: this.position,
      verticalPosition: this.verticalPosition,
      clientEl,
      options,
      contextHoverIdentifiers: this.contextHoverIdentifiers || [this.contextHoverIdentifier],
    });
    if (resp) {
      this._isOpen.set(true);
      this.isOpen.set(true);
      this.onToggle.emit(true);
      this.check.detectChanges();
    }
  }

  async close() {
    if (!this._isOpen()) return;
    this._isOpen.set(false);
    this.contextMenuService.hideContext();
    this.onToggle.emit(false);
    await sleep(200);
    this.isOpen.set(false);
    this.check.detectChanges();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
